import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Button, Space, Modal, message, Spin, Card, Statistic, Row, Col, Select, Input, Tooltip } from 'antd';
import {
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  WhatsAppOutlined,
  FacebookOutlined,
  SearchOutlined,
  MailOutlined
} from '@ant-design/icons';
import { digitalProductPreordersApi } from '../utils/api';
// Accent contextuel : noir dans l'espace privé, bleu en dehors.
import { PRIVATE_PRIMARY_COLOR as PRIMARY_COLOR } from '../theme/privateTheme';
import { getDeviseActive } from '../utils/devise';

const { Option } = Select;
const { confirm } = Modal;

/**
 * Page de gestion des pré-commandes de produits digitaux
 * Permet de voir toutes les pré-commandes et de marquer manuellement les paiements
 */
const DigitalProductPreorders = () => {
  // États pour les données
  const [preorders, setPreorders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // États pour les filtres
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchText, setSearchText] = useState('');

  // États pour les statistiques
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    paid: 0,
    cancelled: 0,
    expired: 0
  });

  /**
   * Récupère toutes les pré-commandes depuis l'API
   * Utilise useCallback pour éviter les recréations inutiles
   */
  const fetchPreorders = useCallback(async () => {
    setLoading(true);
    try {
      // Construire les filtres
      const filters = {};
      if (statusFilter !== 'all') {
        filters.status = statusFilter;
      }

      const response = await digitalProductPreordersApi.getAll(filters);
      
      if (response && response.data) {
        const data = response.data.data || [];
        setPreorders(data);

        // Calculer les statistiques
        const newStats = {
          total: data.length,
          pending: data.filter(p => p.status === 'pending').length,
          paid: data.filter(p => p.status === 'paid').length,
          cancelled: data.filter(p => p.status === 'cancelled').length,
          expired: data.filter(p => p.status === 'expired').length
        };
        setStats(newStats);
      } else {
        message.error('Erreur lors du chargement des pré-commandes');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des pré-commandes:', error);
      message.error('Erreur lors du chargement des pré-commandes');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  /**
   * Charge les pré-commandes au montage et lors du changement de filtre
   * useEffect surveille les changements de fetchPreorders
   */
  useEffect(() => {
    fetchPreorders();
  }, [fetchPreorders]);

  /**
   * Marque une pré-commande comme payée manuellement
   * Déclenche automatiquement l'envoi WhatsApp et le tracking Facebook
   * @param {Object} preorder - La pré-commande à marquer comme payée
   */
  const handleMarkAsPaid = (preorder) => {
    confirm({
      title: 'Confirmer le paiement',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Êtes-vous sûr de vouloir marquer cette pré-commande comme payée ?</p>
          <p><strong>Client :</strong> {preorder.customer_name || preorder.customer_email || preorder.customer_phone}</p>
          <p><strong>Produit :</strong> {preorder.product_name}</p>
          <p><strong>Prix :</strong> {preorder.product_price} {getDeviseActive()}</p>
          <br />
          <p style={{ color: PRIMARY_COLOR }}>
            Cette action va automatiquement :
          </p>
          <ul style={{ color: '#666' }}>
            <li>✓ Marquer la commande comme payée</li>
            <li>✓ Envoyer le lien de téléchargement par WhatsApp</li>
            <li>✓ Tracker l'achat sur Facebook Pixel</li>
          </ul>
        </div>
      ),
      okText: 'Confirmer le paiement',
      cancelText: 'Annuler',
      okType: 'primary',
      onOk: async () => {
        setActionLoading(true);
        try {
          const response = await digitalProductPreordersApi.markAsPaid(preorder.id, {
            amount_paid: preorder.product_price,
            payment_method: 'depot_direct',
            notes: 'Paiement manuel confirmé depuis le backoffice'
          });

          if (response && response.data && response.data.success) {
            const result = response.data.data;
            
            // Message de succès détaillé
            const successMessages = [
              '✅ Paiement confirmé avec succès !',
              result.whatsapp_sent ? '✅ Lien envoyé par WhatsApp' : '⚠️ Envoi WhatsApp échoué',
              result.facebook_tracked ? '✅ Achat tracké sur Facebook' : '⚠️ Tracking Facebook échoué'
            ];

            message.success(
              <div>
                {successMessages.map((msg, idx) => (
                  <div key={idx}>{msg}</div>
                ))}
              </div>,
              5
            );

            // Recharger les données
            await fetchPreorders();
          } else {
            message.error('Erreur lors du marquage du paiement');
          }
        } catch (error) {
          console.error('Erreur lors du marquage du paiement:', error);
          message.error(
            error.response?.data?.message || 
            'Erreur lors du marquage du paiement'
          );
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  /**
   * Renvoie l'email de confirmation pour une pré-commande payée
   * @param {Object} preorder - La pré-commande concernée
   */
  const handleResendEmail = (preorder) => {
    confirm({
      title: 'Renvoyer l\'email de confirmation',
      icon: <MailOutlined />,
      content: (
        <div>
          <p>Voulez-vous envoyer l'email de confirmation à ce client ?</p>
          <p><strong>Email :</strong> {preorder.customer_email}</p>
          <p><strong>Produit :</strong> {preorder.product_name}</p>
        </div>
      ),
      okText: 'Envoyer l\'email',
      cancelText: 'Annuler',
      okType: 'primary',
      onOk: async () => {
        setActionLoading(true);
        try {
          const response = await digitalProductPreordersApi.resendEmail(preorder.id);

          if (response && response.data && response.data.success) {
            message.success('Email de confirmation envoyé avec succès');
            // Recharger les données pour mettre à jour le statut email_sent
            await fetchPreorders();
          } else {
            message.error(response?.data?.message || 'Erreur lors de l\'envoi de l\'email');
          }
        } catch (error) {
          console.error('Erreur lors de l\'envoi de l\'email:', error);
          message.error(
            error.response?.data?.message ||
            'Erreur lors de l\'envoi de l\'email'
          );
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  /**
   * Retourne la couleur du badge en fonction du statut
   * @param {string} status - Le statut de la pré-commande
   * @returns {string} - Couleur du badge
   */
  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      paid: 'green',
      cancelled: 'red',
      expired: 'default'
    };
    return colors[status] || 'default';
  };

  /**
   * Retourne le texte traduit du statut
   * @param {string} status - Le statut en anglais
   * @returns {string} - Texte traduit
   */
  const getStatusText = (status) => {
    const texts = {
      pending: 'En attente',
      paid: 'Payé',
      cancelled: 'Annulé',
      expired: 'Expiré'
    };
    return texts[status] || status;
  };

  /**
   * Filtre les pré-commandes en fonction du texte de recherche
   * Recherche dans : email, téléphone, nom client, nom produit
   */
  const filteredPreorders = preorders.filter(preorder => {
    if (!searchText) return true;
    
    const searchLower = searchText.toLowerCase();
    return (
      (preorder.customer_email && preorder.customer_email.toLowerCase().includes(searchLower)) ||
      (preorder.customer_phone && preorder.customer_phone.toLowerCase().includes(searchLower)) ||
      (preorder.customer_name && preorder.customer_name.toLowerCase().includes(searchLower)) ||
      (preorder.product_name && preorder.product_name.toLowerCase().includes(searchLower)) ||
      (preorder.product_id && preorder.product_id.toLowerCase().includes(searchLower))
    );
  });

  /**
   * Configuration des colonnes du tableau
   * Définit l'affichage de chaque colonne avec ses propriétés
   */
  const columns = [
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '10%',
      render: (date) => new Date(date).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at)
    },
    {
      title: 'Client',
      key: 'customer',
      width: '20%',
      render: (_, record) => (
        <div>
          {record.customer_name && <div><strong>{record.customer_name}</strong></div>}
          {record.customer_email && <div style={{ fontSize: '12px', color: '#666' }}>{record.customer_email}</div>}
          <div style={{ fontSize: '12px', color: PRIMARY_COLOR }}>{record.customer_phone}</div>
        </div>
      ),
      sorter: (a, b) => {
        const nameA = a.customer_name || a.customer_email || a.customer_phone;
        const nameB = b.customer_name || b.customer_email || b.customer_phone;
        return nameA.localeCompare(nameB);
      }
    },
    {
      title: 'Produit',
      key: 'product',
      width: '20%',
      render: (_, record) => (
        <div>
          <div><strong>{record.product_name || 'Produit Digital'}</strong></div>
          <div style={{ fontSize: '12px', color: '#666' }}>ID: {record.product_id}</div>
          <div style={{ fontSize: '12px', color: '#52c41a', fontWeight: 'bold' }}>
            {record.product_price ? `${record.product_price.toLocaleString()} ${getDeviseActive()}` : 'N/A'}
          </div>
        </div>
      )
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: '10%',
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={
          status === 'paid' ? <CheckCircleOutlined /> :
          status === 'pending' ? <ClockCircleOutlined /> :
          status === 'cancelled' ? <CloseCircleOutlined /> :
          <ExclamationCircleOutlined />
        }>
          {getStatusText(status)}
        </Tag>
      ),
      filters: [
        { text: 'En attente', value: 'pending' },
        { text: 'Payé', value: 'paid' },
        { text: 'Annulé', value: 'cancelled' },
        { text: 'Expiré', value: 'expired' }
      ],
      onFilter: (value, record) => record.status === value
    },
    {
      title: 'Paiement',
      key: 'payment',
      width: '15%',
      render: (_, record) => (
        <div>
          {record.status === 'paid' ? (
            <>
              <div style={{ fontSize: '12px', color: '#52c41a' }}>
                <CheckCircleOutlined /> {record.amount_paid?.toLocaleString()} {getDeviseActive()}
              </div>
              <div style={{ fontSize: '11px', color: '#999' }}>
                {new Date(record.paid_at).toLocaleDateString('fr-FR')}
              </div>
              <div style={{ fontSize: '11px', color: '#666' }}>
                {record.payment_provider || 'N/A'}
              </div>
            </>
          ) : (
            <Tag color="orange">En attente</Tag>
          )}
        </div>
      )
    },
    {
      title: 'Tracking',
      key: 'tracking',
      width: '15%',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Tooltip title={record.download_link_sent ? 'Lien envoyé par WhatsApp' : 'Lien non envoyé'}>
            <Tag
              color={record.download_link_sent ? 'green' : 'default'}
              icon={<WhatsAppOutlined />}
              style={{ fontSize: '11px' }}
            >
              WhatsApp
            </Tag>
          </Tooltip>
          <Tooltip title={record.facebook_purchase_tracked ? 'Achat tracké sur Facebook' : 'Non tracké'}>
            <Tag
              color={record.facebook_purchase_tracked ? 'blue' : 'default'}
              icon={<FacebookOutlined />}
              style={{ fontSize: '11px' }}
            >
              Facebook
            </Tag>
          </Tooltip>
          {/* Indicateur email avec bouton de renvoi si non envoyé */}
          <Tooltip title={record.email_sent ? 'Email envoyé' : 'Email non envoyé - Cliquez pour envoyer'}>
            {record.email_sent ? (
              <Tag
                color="green"
                icon={<MailOutlined />}
                style={{ fontSize: '11px' }}
              >
                Email
              </Tag>
            ) : (
              // Bouton pour envoyer l'email si non envoyé et commande payée avec email disponible
              record.status === 'paid' && record.customer_email ? (
                <Button
                  type="default"
                  size="small"
                  icon={<MailOutlined />}
                  onClick={() => handleResendEmail(record)}
                  loading={actionLoading}
                  style={{ fontSize: '11px', padding: '0 8px' }}
                >
                  Envoyer email
                </Button>
              ) : (
                <Tag
                  color="default"
                  icon={<MailOutlined />}
                  style={{ fontSize: '11px' }}
                >
                  Email
                </Tag>
              )
            )}
          </Tooltip>
        </Space>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '15%',
      fixed: 'right',
      render: (_, record) => (
        <Space>
          {record.status === 'pending' && (
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => handleMarkAsPaid(record)}
              loading={actionLoading}
            >
              Marquer comme payé
            </Button>
          )}
          {record.status === 'paid' && (
            <Tag color="green" icon={<CheckCircleOutlined />}>
              Payé
            </Tag>
          )}
        </Space>
      )
    }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* En-tête avec titre et bouton de rechargement */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pré-commandes Produits Digitaux</h1>
          <p className="text-gray-500">
            Gérez les pré-commandes et confirmez les paiements manuels (dépôts directs, etc.)
          </p>
        </div>
        <Button 
          icon={<ReloadOutlined />} 
          onClick={fetchPreorders}
          loading={loading}
        >
          Actualiser
        </Button>
      </div>

      {/* Cartes de statistiques */}
      <Row gutter={16}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic 
              title="Total" 
              value={stats.total} 
              valueStyle={{ color: PRIMARY_COLOR }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic 
              title="En attente" 
              value={stats.pending} 
              valueStyle={{ color: '#faad14' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic 
              title="Payées" 
              value={stats.paid} 
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic 
              title="Annulées/Expirées" 
              value={stats.cancelled + stats.expired} 
              valueStyle={{ color: '#ff4d4f' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filtres et recherche */}
      <Card>
        <div className="flex flex-wrap gap-4 mb-4">
          <div className="flex-1 min-w-[200px]">
            <Input
              placeholder="Rechercher (email, téléphone, nom, produit...)"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </div>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 200 }}
          >
            <Option value="all">Tous les statuts</Option>
            <Option value="pending">En attente</Option>
            <Option value="paid">Payé</Option>
            <Option value="cancelled">Annulé</Option>
            <Option value="expired">Expiré</Option>
          </Select>
        </div>

        {/* Tableau des pré-commandes */}
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={filteredPreorders}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} pré-commandes`,
              pageSizeOptions: ['10', '20', '50', '100']
            }}
            scroll={{ x: 1200 }}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default DigitalProductPreorders;
